import { t } from "i18next";
import { DeveloperResources } from "@/modules/developer_resources/developer_resources.entity";
import { DictValue } from "@/modules/dict_value/dict_value.entity";
import "../../components/i18n/main";
interface State {
	allProductTypes: DictValue[];
	allProductCategories: ProductCategory[];
	dataList: (DeveloperResources & { enFile?: string })[];
	keyword: string;
	type: string;
	productType: string;
}

interface ProductCategory {
	label: string;
	value: string;
	total: number;
}

type DeveloperResourcesWindow = Window & {
	ALL_PRODUCT_TYPES?: DictValue[];
	ALL_PRODUCT_CATEGORIES?: ProductCategory[];
	ALL_DEVELOPER_RESOURCES?: (DeveloperResources & { enFile?: string })[];
};

(() => {
	const pageWindow = window as DeveloperResourcesWindow;
	const PRODUCT_RESOURCE_TYPE = "product_resources";
	// 1. 初始化 State
	let state: State = {
		allProductTypes: pageWindow.ALL_PRODUCT_TYPES || [],
		allProductCategories: pageWindow.ALL_PRODUCT_CATEGORIES || [],
		dataList: pageWindow.ALL_DEVELOPER_RESOURCES || [],
		keyword: "",
		type: "",
		productType: "",
	};

	// DOM Elements
	const searchInput = document.querySelector(
		".search-input",
	) as HTMLInputElement;
	const typeGroup = document.querySelector(".type-group");
	const productTypeGroup = document.querySelector<HTMLDivElement>(
		".product-type-group",
	);
	const tableBody = document.querySelector(".resources-table .table-body");

	// 2. 初始化
	init();

	function init() {
		const searchParams = new URLSearchParams(window.location.search);
		state.type = searchParams.get("type") || "";
		state.productType = searchParams.get("s_type") || "";
		state.keyword = searchInput?.value || "";

		bindEvents();
		render();
	}

	// 3. 绑定事件
	function bindEvents() {
		if (searchInput) {
			searchInput.addEventListener("keyup", (e) => {
				if (e.key === "Enter") {
					updateState({ keyword: searchInput.value.trim() });
				}
			});
			searchInput.addEventListener("blur", () => {
				updateState({ keyword: searchInput.value.trim() });
			});
		}

		typeGroup?.querySelectorAll(".filter-item").forEach((item) => {
			item.addEventListener("click", () => {
				const val = item.getAttribute("data-value") || "";
				// 切换类型时，重置子类型
				if (state.type !== val) {
					updateState({ type: val, productType: "" });
				}
			});
		});

		// 事件委托
		productTypeGroup?.addEventListener("click", (e) => {
			const target = e.target as HTMLElement;
			if (target.classList.contains("filter-item")) {
				const val = target.getAttribute("data-value") || "";
				if (state.productType !== val) {
					updateState({ productType: val });
				}
			}
		});
	}

	// 4. 更新 State
	function updateState(payload: Partial<State>) {
		state = { ...state, ...payload };
		render();
	}

	// 5. Render 方法
	function render() {
		updateUrl();
		renderFilters();
		renderTable();
	}

	function updateUrl() {
		const searchParams = new URLSearchParams(window.location.search);
		if (state.type) {
			searchParams.set("type", state.type);
		} else {
			searchParams.delete("type");
		}
		if (state.productType) {
			searchParams.set("s_type", state.productType);
		} else {
			searchParams.delete("s_type");
		}
		const newUrl = `${window.location.pathname}?${searchParams.toString()}`;
		window.history.replaceState({}, "", newUrl);
	}

	function getFilteredList(keyword: string, type: string, productType: string) {
		return state.dataList.filter((item) => {
			const titleMatch = item.title
				?.toLowerCase()
				.includes(keyword.toLowerCase());
			const typeMatch = type ? item.type === type : true;
			const productTypeMatch = productType
				? item.product_type === productType
				: true;
			return titleMatch && typeMatch && productTypeMatch;
		});
	}

	function renderFilters() {
		// 渲染一级分类高亮
		typeGroup?.querySelectorAll(".filter-item").forEach((item) => {
			const val = item.getAttribute("data-value") || "";
			if (val === state.type) {
				item.classList.add("active");
			} else {
				item.classList.remove("active");
			}
		});

		// 渲染二级分类
		if (!productTypeGroup) return;

		// 计算可选的二级分类 (基于当前一级分类，忽略当前二级分类选择)
		const listForFilters = getFilteredList(state.keyword, state.type, "");
		const productTypes = [
			...new Set(listForFilters.map((i) => i.product_type)),
		];

		if (state.type === "" || productTypes.length <= 1) {
			productTypeGroup.style.display = "none";
			productTypeGroup.innerHTML = "";
			return;
		}

		productTypeGroup.style.display = "flex";

		// 产品资源分类使用已上架的产品分类
		const categories: {
			label: string;
			value: string;
			total?: number;
		}[] =
			state.type === PRODUCT_RESOURCE_TYPE
				? state.allProductCategories
				: state.allProductTypes;

		// 生成 HTML
		const html = [
			{ value: "", label: t("全部"), total: listForFilters.length },
			...categories.filter((item) => productTypes.includes(item.value)),
		]
			.map(
				(i) => `
        <div class="filter-item ${i.value === state.productType ? "active" : ""}" data-value="${i.value}">${i.label}<span class="filter-count">${i.total ?? ""}</span></div>
        `,
			)
			.join("");

		if (productTypeGroup.innerHTML !== html) {
			productTypeGroup.innerHTML = html;
		}
	}

	function renderTable() {
		const list = getFilteredList(state.keyword, state.type, state.productType);
		if (!tableBody) return;

		tableBody.innerHTML = list
			.map((item) => {
				const isFile = item.resource_type === "file";
				const cnAction = isFile
					? item.file
						? `<a href="${item.file}" target="_blank" ><img class="file-icon" src="/imgs/icon-pdf2.svg" /></a>`
						: ""
					: "-";
				const enAction = isFile
					? item.enFile
						? `<a href="${item.enFile}?a.pdf" target="_blank"><img class="file-icon" src="/imgs/icon-pdf2.svg" /></a>`
						: ""
					: "-";
				const title = isFile
					? `<span class="resource-name">${item.title}</span>`
					: `<a class="resource-name resource-link" href="/developer-resources/${item.id}">${item.title}</a>`;
				return `
<div class="table-row">
  <div class="col col-name">
    ${title}
  </div>
  <div class="col col-cn">
    ${cnAction}
  </div>
  <div class="col col-en">
    ${enAction}
  </div>
  <div class="col col-date">${item.push_date}</div>
</div>
      `;
			})
			.join("");
	}
})();
